// The first gate of an HTML plan (docs/architecture/plans-and-docs.md, §5):
// a private DOMPurify instance configured by allowlist. Agents that read
// untrusted text write plans, and a plan is drawn in the app's own origin,
// where passkey approvals happen: it must not run script, load anything from
// elsewhere, draw the app's controls or clobber the DOM.
//
// What it keeps, and nothing else:
// - the elements of today's template and of a plan's words (ELEMENTS);
// - the attributes of ATTRIBUTES and `aria-*`; `class` with its `pp-`
//   tokens alone; `id` and `name` only as DOMPurify prefixes them
//   (`user-content-…`, SANITIZE_NAMED_PROPS);
// - a link to `https:`, `http:`, `mailto:`, `#…` or a path on this origin;
//   an image written in place as a raster `data:` URL, or a path on this
//   origin. A protocol-relative `//host` is no path.
// Script, style, template, noscript, frames, objects, SVG, MathML, forms and
// media go with all they hold (DROPPED).
//
// The page reads the sanitised fragment (RETURN_DOM_FRAGMENT) and draws it as
// React elements through a second allowlist (html.tsx): no string it makes is
// ever parsed again into the page.
import DOMPurify, { type Config, type DOMPurify as Purifier } from 'dompurify'

/** The prefix DOMPurify gives every id and name it keeps. */
export const USER_CONTENT_PREFIX = 'user-content-'

/** The elements a plan may hold. */
export const ELEMENTS: readonly string[] = [
  // text and structure
  'p', 'section', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'div', 'br', 'hr',
  // inline
  'span', 'strong', 'em', 'b', 'i', 'u', 's', 'code', 'pre', 'time',
  // blocks
  'blockquote', 'details', 'summary', 'a', 'img',
  // tables
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
]

/** The attributes a plan may carry, `aria-*` besides. `id` and `name` are kept only as DOMPurify prefixes them. */
export const ATTRIBUTES: readonly string[] = [
  'class', 'data-met', 'data-on', 'data-state', 'href', 'title', 'alt', 'src',
  'colspan', 'rowspan', 'datetime', 'open', 'lang', 'dir', 'id', 'name',
]

/** Dropped with all they hold: what runs, styles, frames, draws or loads. */
export const DROPPED: readonly string[] = [
  'script', 'style', 'template', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'svg', 'math', 'form', 'select', 'textarea', 'button',
  'audio', 'video', 'source', 'track', 'picture', 'canvas', 'map', 'area',
  // DOMPurify's own list: a head, a title and the rest of what never holds words.
  'head', 'title', 'xmp', 'plaintext', 'noembed', 'noframes', 'annotation-xml', 'foreignobject', 'desc', 'mi', 'mn', 'mo', 'ms', 'mtext', 'colgroup',
]

/** A class of the plan's own: a whole `pp-` token. */
export const PLAN_CLASS = /^pp-[a-z][a-z-]*$/

const HTML_SPACE = /[\t\n\f\r ]+/
/** What a browser strips from a URL before it reads it, and what it reads as nothing. */
const URL_IGNORED = /[\u0000-\u0020\u007f-\u009f\u00ad\u200b-\u200f\u2028\u2029\ufeff]/g
const LINK = /^(?:https?:|mailto:|#)/i
/** A path on this origin: one slash, then anything but another slash or a backslash (`//host` and `/\host` name another host). */
const OWN_PATH = /^\/(?![/\\])/
/** A raster image written in place. */
const RASTER_DATA = /^data:image\/(?:png|jpe?g|gif|webp)[;,]/i

/** Whether a link may go there: the web's and mail's schemes, an anchor, or a path on this origin. */
export function safeHref(value: string): boolean {
  const url = value.replace(URL_IGNORED, '')
  return LINK.test(url) || OWN_PATH.test(url)
}

/** Whether an image may come from there: written in place as a raster, or a path on this origin. Nothing is fetched from elsewhere. */
export function safeImage(value: string): boolean {
  const url = value.replace(URL_IGNORED, '')
  return RASTER_DATA.test(url) || OWN_PATH.test(url)
}

/** A class attribute's own tokens, or null when none is left. */
export function planClasses(value: string): string | null {
  const kept = value.split(HTML_SPACE).filter((token) => PLAN_CLASS.test(token))
  return kept.length ? kept.join(' ') : null
}

const CONFIG: Config = {
  ALLOWED_TAGS: [...ELEMENTS],
  ALLOWED_ATTR: [...ATTRIBUTES],
  ALLOW_ARIA_ATTR: true,
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // DOMPurify reads every attribute but these as a URL: they hold none.
  ADD_URI_SAFE_ATTR: ['data-met', 'data-on', 'data-state', 'colspan', 'rowspan', 'datetime', 'open', 'lang', 'dir'],
  // The hooks check every URL themselves; this is the same rule, once more.
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#|\/(?![/\\])|data:image\/(?:png|jpe?g|gif|webp)[;,])/i,
  FORBID_CONTENTS: [...DROPPED],
  KEEP_CONTENT: true,
  SANITIZE_NAMED_PROPS: true,
  WHOLE_DOCUMENT: false,
}

let instance: Purifier | null = null

function purifier(): Purifier {
  if (instance) return instance
  // No setConfig: a configuration set so would ignore the one each call gives, and a call asks for a fragment.
  const purify = DOMPurify(window)
  purify.addHook('uponSanitizeAttribute', (node, event) => {
    const name = event.attrName
    const value = event.attrValue
    if (name === 'class') {
      const kept = planClasses(value)
      if (kept === null) event.keepAttr = false
      else event.attrValue = kept
    } else if (name === 'href') {
      if (node.nodeName !== 'A' || !safeHref(value)) event.keepAttr = false
    } else if (name === 'src') {
      if (node.nodeName !== 'IMG' || !safeImage(value)) event.keepAttr = false
    } else if (name.startsWith('data-') && name !== 'data-met' && name !== 'data-on' && name !== 'data-state') {
      event.keepAttr = false
    }
  })
  instance = purify
  return purify
}

/** An HTML plan's body, sanitised, as an inert fragment: the doctype and the head are gone, the body's content is left. */
export function sanitizePlanFragment(html: string): DocumentFragment {
  return purifier().sanitize(html, { ...CONFIG, RETURN_DOM_FRAGMENT: true })
}
